if (!customElements.get('blog-infinite-scroll')) {
  customElements.define(
    'blog-infinite-scroll',
    class BlogInfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.observer = null;
        this.onIntersect = this.onIntersect.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onRetry = this.onRetry.bind(this);
      }

      get gridId() {
        return `blog-articles-grid-${this.sectionId}`;
      }

      get rootMarginPx() {
        const raw = parseInt(this.dataset.rootMargin, 10);
        return Number.isFinite(raw) ? raw : 200;
      }

      get rootMargin() {
        return `${this.rootMarginPx}px`;
      }

      connectedCallback() {
        this.grid = this.querySelector('[data-blog-articles-grid]');

        if (this.dataset.enabled !== 'true') return;

        this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
        this.loader = this.querySelector('[data-infinite-scroll-loader]');
        this.statusEl = this.querySelector('[data-infinite-scroll-status]');
        this.retryButton = this.querySelector('[data-infinite-scroll-retry]');
        this.sectionId = this.dataset.sectionId || '';
        this.currentPage = parseInt(this.dataset.currentPage || '1', 10);
        this.totalPages = parseInt(this.dataset.totalPages || '1', 10);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (
          !this.grid ||
          !this.isValidSectionId(this.sectionId) ||
          !this.hasMorePages() ||
          !this.sentinel
        ) {
          return;
        }

        this.retryButton?.addEventListener('click', this.onRetry);

        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          rootMargin: this.rootMargin,
          threshold: 0,
        });
        this.observer.observe(this.sentinel);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        requestAnimationFrame(() => this.checkViewport());
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.retryButton?.removeEventListener('click', this.onRetry);
      }

      isValidSectionId(id) {
        return typeof id === 'string' && /^[A-Za-z0-9_-]+$/.test(id);
      }

      isValidPage(page) {
        return Number.isInteger(page) && page >= 1 && page <= 500;
      }

      isSameOriginUrl(url) {
        try {
          return new URL(url, window.location.origin).origin === window.location.origin;
        } catch {
          return false;
        }
      }

      hasMorePages() {
        return this.totalPages > this.currentPage;
      }

      isSentinelNearViewport() {
        if (!this.sentinel) return false;
        const rect = this.sentinel.getBoundingClientRect();
        return rect.top <= window.innerHeight + this.rootMarginPx;
      }

      checkViewport() {
        if (this.isSentinelNearViewport()) {
          this.loadMore();
        }
      }

      onScroll() {
        if (this.loading || !this.hasMorePages()) return;
        if (this.isSentinelNearViewport()) {
          this.loadMore();
        }
      }

      onIntersect(entries) {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore();
        }
      }

      onRetry() {
        this.hideError();
        this.loadMore();
      }

      buildFetchUrl(page) {
        if (!this.isValidPage(page)) {
          throw new Error('Invalid page number');
        }

        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', this.sectionId);
        url.searchParams.delete('sections');

        if (!this.isSameOriginUrl(url.toString())) {
          throw new Error('Invalid fetch URL');
        }

        return url;
      }

      parseSectionHtml(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sectionWrapper = doc.getElementById(`shopify-section-${this.sectionId}`);
        const scopes = [sectionWrapper, doc].filter(Boolean);

        let sourceGrid = null;
        let sourceRoot = null;

        for (const scope of scopes) {
          sourceGrid =
            scope.getElementById?.(this.gridId) ||
            scope.querySelector?.(`#${CSS.escape(this.gridId)}`) ||
            scope.querySelector?.('[data-blog-articles-grid]');
          sourceRoot = scope.querySelector?.('blog-infinite-scroll');
          if (sourceGrid) break;
        }

        return { sourceRoot, sourceGrid };
      }

      async loadMore() {
        if (this.loading) return;

        if (!this.hasMorePages()) {
          this.complete();
          return;
        }

        const nextPage = this.currentPage + 1;
        if (!this.isValidPage(nextPage)) {
          this.showError();
          return;
        }

        this.loading = true;
        this.observer?.unobserve(this.sentinel);
        this.setBusy(true);
        this.hideError();

        try {
          const response = await fetch(this.buildFetchUrl(nextPage).toString(), {
            credentials: 'same-origin',
          });

          if (!response.ok) throw new Error(`Request failed: ${response.status}`);

          const html = await response.text();
          if (!html) throw new Error('Empty section response');

          const { sourceRoot, sourceGrid } = this.parseSectionHtml(html);

          if (!sourceGrid) throw new Error('Articles grid not found in response');

          const newItems = [...sourceGrid.querySelectorAll('.blog-articles__article')];

          if (newItems.length === 0) {
            this.complete();
            return;
          }

          this.appendItems(newItems);

          this.currentPage = nextPage;
          this.dataset.currentPage = String(nextPage);

          const responseTotalPages = parseInt(sourceRoot?.dataset.totalPages || '', 10);
          if (Number.isFinite(responseTotalPages) && responseTotalPages > 0) {
            this.totalPages = responseTotalPages;
            this.dataset.totalPages = String(responseTotalPages);
          }

          if (!this.hasMorePages()) {
            this.complete();
          } else if (this.sentinel && this.observer) {
            this.observer.observe(this.sentinel);
            requestAnimationFrame(() => this.checkViewport());
          }
        } catch {
          this.showError();
        } finally {
          this.loading = false;
          this.setBusy(false);
        }
      }

      appendItems(items) {
        const staggerMax = 6;

        items.forEach((item, index) => {
          const article = document.importNode(item, true);

          if (!this.reducedMotion) {
            article.classList.add('main-blog__article--revealing');
            article.style.setProperty('--reveal-delay', `${(index % staggerMax) * 80}ms`);
          }

          this.grid.appendChild(article);

          if (!this.reducedMotion) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                article.classList.add('main-blog__article--revealed');
              });
            });
          } else {
            article.classList.add('main-blog__article--revealed');
          }
        });
      }

      complete() {
        this.observer?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.sentinel?.remove();
        this.setLoading(false);

        if (this.dataset.showEndMessage === 'true' && this.statusEl) {
          this.statusEl.hidden = false;
          this.statusEl.textContent = this.dataset.endMessage || '';
        }
      }

      setBusy(isBusy) {
        this.grid?.setAttribute('aria-busy', isBusy ? 'true' : 'false');
        this.setLoading(isBusy);
      }

      setLoading(isLoading) {
        if (!this.loader || this.dataset.showLoading !== 'true') return;
        this.loader.hidden = !isLoading;
      }

      showError() {
        this.observer?.disconnect();

        if (this.statusEl) {
          this.statusEl.hidden = false;
          this.statusEl.textContent = this.dataset.errorMessage || '';
        }

        if (this.retryButton) {
          this.retryButton.hidden = false;
        }
      }

      hideError() {
        if (this.statusEl) {
          this.statusEl.hidden = true;
          this.statusEl.textContent = '';
        }

        if (this.retryButton) {
          this.retryButton.hidden = true;
        }

        if (this.sentinel && this.observer && this.hasMorePages()) {
          this.observer.observe(this.sentinel);
        }
      }
    }
  );
}
